<template>
  <div class="pulse-health-analysis">
    <div class="pulse-card-container">
       <div class="pulse-summary-panel">
        <div class="pulse-summary-row">
          <span class="pulse-summary-label">平均血压</span>
          <span class="pulse-summary-value">{{ healthAnalysis.avgBloodPressure || '--' }}</span>
          <span class="pulse-summary-unit">mmHg</span>
          <span class="pulse-summary-status" :class="statusClass(avgBloodPressureStatus)">
            {{ avgBloodPressureStatus || '' }}
          </span>
        </div>
        <div class="pulse-summary-sub">平均脉搏 {{ heartRateDisplay }}</div>
      </div>

      <div class="pulse-chart-wrap" v-if="bloodPressureChart.points.length">
        <div class="pulse-chart-main">
          <div class="pulse-grid-row" v-for="line in 4" :key="line"></div>
          <div class="pulse-grid-col" v-for="line in 3" :key="`c-${line}`"></div>
          <svg class="pulse-chart-svg" viewBox="0 0 320 220" preserveAspectRatio="none">
            <polyline
              :points="bloodPressureChart.upPoints"
              fill="none"
              stroke="#f49a2d"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <polyline
              :points="bloodPressureChart.downPoints"
              fill="none"
              stroke="#59aee7"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <circle
              v-for="(point, index) in bloodPressureChart.points"
              :key="`up-${index}`"
              :cx="point.x"
              :cy="point.upY"
              r="4"
              fill="#f49a2d"
              stroke="#8e5a1a"
              stroke-width="1.5"
            />
            <circle
              v-for="(point, index) in bloodPressureChart.points"
              :key="`down-${index}`"
              :cx="point.x"
              :cy="point.downY"
              r="4"
              fill="#59aee7"
              stroke="#2b6d97"
              stroke-width="1.5"
            />
          </svg>
          <div class="pulse-chart-x-axis">
            <span
              v-for="item in bloodPressureChart.xLabels"
              :key="item.key"
              :style="{ left: item.left }"
            >
              {{ item.label }}
            </span>
          </div>
        </div>
        <div class="pulse-chart-y-axis">
          <span v-for="label in bloodPressureChart.yLabels" :key="label">{{ label }}</span>
        </div>
      </div>

      <div class="pulse-text-block" v-if="healthAnalysis.fluctuationCurveAnalysis">
        <span class="pulse-label-strong">波动曲线分析：</span>{{ healthAnalysis.fluctuationCurveAnalysis }}
      </div>
      <div class="pulse-text-block" v-if="healthAnalysis.aiMoreAnalysis">
        <span class="pulse-label-strong">AI多模态关联分析：</span>{{ healthAnalysis.aiMoreAnalysis }}
      </div>

      <div class="bottom-statement">
        重要声明：本报告采用AI人工智能分析总结，仅供参考，不可作为医疗诊断结论。如已有身体不适，请及时咨询医生。
        <div class="time-text" v-if="reportData.createAt">生成时间：{{ reportData.createAt }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PulseHealthAnalysis',
  props: {
    reportData: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    healthAnalysis() {
      return this.reportData?.pulseReport?.healthAnalysis || {};
    },
    avgBloodPressureStatus() {
      const pressure = String(this.healthAnalysis?.avgBloodPressure || '');
      const matched = pressure.match(/(\d+)\s*\/\s*(\d+)/);
      if (!matched) return '';
      const systolic = Number(matched[1]);
      const diastolic = Number(matched[2]);
      if (systolic >= 140 || diastolic >= 90) return '偏高';
      if (systolic >= 120 || diastolic >= 80) return '正常高值';
      if (systolic < 90 || diastolic < 60) return '偏低';
      return '正常';
    },
    heartRateDisplay() {
      const heartRate = this.healthAnalysis?.avgHeartRate;
      if (!heartRate) return '--';
      return String(heartRate).includes('/分钟') ? heartRate : `${heartRate}/分钟`;
    },
    bloodPressureChart() {
      const list = Array.isArray(this.healthAnalysis?.bloodPressure) ? this.healthAnalysis.bloodPressure : [];
      if (!list.length) {
        return { points: [], upPoints: '', downPoints: '', xLabels: [], yLabels: ['200', '160', '120', '90', '40'] };
      }

      const width = 320;
      const height = 220;
      const paddingX = 26;
      const minY = 40;
      const maxY = 200;
      const step = list.length > 1 ? (width - paddingX * 2) / (list.length - 1) : 0;
      const mapY = (value) => {
        const numeric = Number(value || minY);
        const ratio = (numeric - minY) / (maxY - minY);
        return height - 18 - (ratio * (height - 36));
      };

      const points = list.map((item, index) => ({
        x: Number((paddingX + step * index).toFixed(2)),
        upY: Number(mapY(item.up).toFixed(2)),
        downY: Number(mapY(item.down).toFixed(2)),
        time: item.time || ''
      }));

      return {
        points,
        upPoints: points.map(item => `${item.x},${item.upY}`).join(' '),
        downPoints: points.map(item => `${item.x},${item.downY}`).join(' '),
        xLabels: [
          { key: '00:00', label: '00:00', left: '0%' },
          { key: '06:00', label: '06:00', left: '25%' },
          { key: '12:00', label: '12:00', left: '50%' },
          { key: '18:00', label: '18:00', left: '75%' }
        ],
        yLabels: ['200', '160', '120', '90', '40']
      };
    }
  },
  methods: {
    statusClass(status) {
      const text = status || '';
      if (text.includes('正常')) return 'is-normal';
      if (text.includes('偏高') || text.includes('偏低') || text.includes('中高')) return 'is-warn';
      if (text.includes('异常') || text.includes('较高') || text.includes('高风险')) return 'is-danger';
      return '';
    }
  }
};
</script>

<style lang="scss" scoped>
.pulse-card-container {
  min-height: calc(100vh - 130px);
  background: #fff;
  padding: 24px 16px 32px;
}

.pulse-summary-panel {
  text-align: center;
  padding-top: 36px;
}

.pulse-summary-row {
  font-size: 18px;
  color: #333;
  display: flex;
  align-items: baseline;
  justify-content: center;
  flex-wrap: wrap;
  gap: 6px;
}

.pulse-summary-value {
  font-size: 34px;
  line-height: 1;
  font-weight: 700;
}

.pulse-summary-label {
  font-size: 18px;
  color: #333;
}

.pulse-summary-unit {
  font-size: 17px;
  color: #333;
}

.pulse-summary-status {
  font-size: 17px;
}

.pulse-summary-sub {
  font-size: 18px;
  color: #333;
}

.pulse-summary-sub {
  margin-top: 8px;
}

.pulse-chart-wrap {
  margin-top: 30px;
  display: flex;
  align-items: flex-start;
  gap: 0;
}

.pulse-chart-y-axis {
  width: 34px;
  height: 312px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  font-size: 14px;
  color: #3d5066;
  padding-top: 2px;
  padding-bottom: 36px;
  padding-left: 8px;
}

.pulse-chart-main {
  position: relative;
  flex: 1;
  height: 312px;
  border-left: 1px solid #d8d8d8;
  border-right: 1px solid #d8d8d8;
  border-bottom: 1px solid #d8d8d8;
  border-top: 1px solid #d8d8d8;
}

.pulse-grid-row,
.pulse-grid-col {
  position: absolute;
  background: #d8d8d8;
}

.pulse-grid-row {
  left: 0;
  right: 0;
  height: 1px;
}

.pulse-grid-row:nth-child(1) { top: 20%; }
.pulse-grid-row:nth-child(2) { top: 40%; }
.pulse-grid-row:nth-child(3) { top: 60%; }
.pulse-grid-row:nth-child(4) { top: 80%; }

.pulse-grid-col {
  top: 0;
  bottom: 0;
  width: 1px;
}

.pulse-grid-col:nth-child(5) { left: 25%; }
.pulse-grid-col:nth-child(6) { left: 50%; }
.pulse-grid-col:nth-child(7) { left: 75%; }

.pulse-chart-svg {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  width: 100%;
  height: 256px;
}

.pulse-chart-x-axis {
  position: absolute;
  left: 0;
  right: 0;
  bottom: -2px;
  height: 36px;
  font-size: 14px;
  color: #3d5066;
}

.pulse-chart-x-axis span {
  position: absolute;
  bottom: 0;
  transform: translateX(-50%);
}

.pulse-text-block {
  font-size: 16px;
  line-height: 1.85;
  color: #333;
  margin-top: 28px;
}

.pulse-label-strong {
  font-weight: 700;
}

.is-normal {
  color: #13a35d;
}

.is-warn {
  color: #ff7a1a;
}

.is-danger {
  color: #ff2b12;
}

.bottom-statement {
  margin-top: 40px;
  font-size: 13px;
  line-height: 1.7;
  color: #a0a4aa;
}

.time-text {
  margin-top: 8px;
}
</style>
